/* eslint-disable */
/**
 * PKCE Helper - Generate code_verifier and code_challenge for OAuth2 PKCE flow
 * Based on RFC 7636 - Proof Key for Code Exchange
 */

/**
 * Generate random string for code_verifier
 * Requirements: 43-128 characters, [A-Z] [a-z] [0-9] - . _ ~
 */
function generateCodeVerifier() {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const length = 128; // Max length for better security
  let verifier = '';
  
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);
  
  for (let i = 0; i < length; i++) {
    verifier += charset[randomValues[i] % charset.length];
  }
  
  return verifier;
}

/**
 * Generate code_challenge from code_verifier using SHA-256
 * Method: S256 (SHA-256 hash, then base64url encode)
 */
async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  
  return base64UrlEncode(hash);
}

/**
 * Base64 URL encode (without padding)
 */
function base64UrlEncode(arrayBuffer) {
  const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate random state for OAuth2 security
 */
function generateState() {
  const randomValues = new Uint8Array(16);
  window.crypto.getRandomValues(randomValues);
  return Array.from(randomValues, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate PKCE pair (verifier + challenge) and state
 */
async function generatePKCE() {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();
  
  return {
    codeVerifier,
    codeChallenge,
    codeChallengeMethod: 'S256',
    state
  };
}

/**
 * Store PKCE data in sessionStorage (temporary, more secure than localStorage)
 */
function storePKCE(pkceData) {
  sessionStorage.setItem('pkce_verifier', pkceData.codeVerifier);
  sessionStorage.setItem('pkce_state', pkceData.state);
}

/**
 * Retrieve and clear PKCE data from sessionStorage
 */
function retrieveAndClearPKCE() {
  const codeVerifier = sessionStorage.getItem('pkce_verifier');
  const state = sessionStorage.getItem('pkce_state');
  
  // Clear after retrieval for security
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('pkce_state');
  
  return { codeVerifier, state };
}

/**
 * Validate state to prevent CSRF attacks
 */
function validateState(receivedState) {
  const storedState = sessionStorage.getItem('pkce_state');
  return receivedState === storedState;
}

const PKCEHelper = {
  generatePKCE,
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  storePKCE,
  retrieveAndClearPKCE,
  validateState,
  base64UrlEncode
};

export default PKCEHelper;
